// ProductDetail.js

import React, { useEffect, useState, useContext } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { buildProductPath, parseProductSlug } from '../utils/productSlug';
import { doc, getDoc, collection, getDocs, onSnapshot } from 'firebase/firestore';
import { db } from '../firebase';
import usePageView from '../hooks/usePageView';
import { StoreContext } from '../contexts/StoreContext';
import Loading from './Loading';
import ProductGridItem from './ProductGridItem';
import { useDisplayPreferences } from '../contexts/DisplayPreferencesContext';
import { MAX_CONTENT_WIDTH, BORDER_RADIUS, SPACING_BUTTON, SPACING_CARD_BODY, SPACING_COMPONENT, SPACING_CARD } from '../constants/layout';
import {
  FONT_HEADLINE,
  FONT_BODY,
  FONT_CALLOUT,
  FONT_FOOTNOTE,
  FONT_SUBHEADLINE,
} from '../constants/typography';

const formatPrice = (amount, currency) =>
  new Intl.NumberFormat(undefined, {
    style: 'currency',
    currency,
    minimumFractionDigits: 2,
  }).format(amount / 100);

const formatDate = timestamp => {
  const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
  return new Intl.DateTimeFormat(undefined, {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  }).format(date);
};

const parseDate = value =>
  !value ? null : value.toDate ? value.toDate() : new Date(value);

function FadeInImage({ src, alt, onClick, className = '' }) {
  const [loaded, setLoaded] = useState(false);

  return (
    <div className={`image-container ${className}`}>
      <img
        src={src}
        alt={alt}
        onLoad={() => setLoaded(true)}
        onClick={onClick}
        className={`fade-img ${loaded ? 'loaded' : ''} clickable-image`}
      />
    </div>
  );
}

export default function ProductDetail() {
  const { slug } = useParams();
  const id = parseProductSlug(slug);
  const navigate = useNavigate();
  usePageView('product', id);
  const { showPrices } = useDisplayPreferences();
  const { store, loading: storeLoading } = useContext(StoreContext);
  const storePhone = store?.storeContact?.phoneNumber ?? '';
  const storeEmail = store?.storeContact?.email ?? '';

  const [product, setProduct] = useState(null);
  const [productImages, setProductImages] = useState([]);
  const [coverImage, setCoverImage] = useState(null);
  const [previewImage, setPreviewImage] = useState(null);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [imageLoading, setImageLoading] = useState(false);

  const [allProducts, setAllProducts] = useState([]);
  const [allCategories, setAllCategories] = useState([]);
  const [allImages, setAllImages] = useState([]);
  // const [loadedImages, setLoadedImages] = useState({});

  // Default to expanded
  const [showDescription, setShowDescription] = useState(true);
  // Default to expanded
  const [showSpecs, setShowSpecs] = useState(true);
  const [expandedGroups, setExpandedGroups] = useState({});

  useEffect(() => {
    if (!product?.modifierGroups) return;
    setExpandedGroups(prev => {
      const updated = { ...prev };
      let changed = false;
      product.modifierGroups.forEach(group => {
        if (updated[group.id] === undefined) {
          updated[group.id] = true;
          changed = true;
        }
      });
      const validIds = new Set(product.modifierGroups.map(group => group.id));
      Object.keys(updated).forEach(id => {
        if (!validIds.has(id)) {
          delete updated[id];
          changed = true;
        }
      });
      return changed ? updated : prev;
    });
  }, [product?.modifierGroups]);

  // Fetch the main product
  useEffect(() => {
    async function loadProduct() {
      const ref = doc(db, 'products', id);
      const snap = await getDoc(ref);
      if (snap.exists()) {
        setProduct({ id: snap.id, ...snap.data() });
      }
    }
    loadProduct();
  }, [id]);

  // Real-time listener for imageItems for THIS product
  useEffect(() => {
    if (!id) return;

    const unsub = onSnapshot(collection(db, 'imageItems'), snap => {
      const allImgs = snap.docs.map(d => ({
        id: d.id,
        caption: d.data().caption ?? '',
        imageURL: d.data().imageURL ?? '',
        productID: d.data().productID ?? null,
        dateAdded: parseDate(d.data().dateAdded),
      }));

      setAllImages(allImgs);

      const filtered = allImgs.filter(img => img.productID === id);
      setProductImages(filtered);

      let cover = null;
      if (product?.productImage?.imageItemID) {
        cover = allImgs.find(
          img => img.id === product.productImage.imageItemID
        );
      }
      if (!cover && filtered.length > 0) {
        cover = filtered[0];
      }
      setCoverImage(cover);
    });

    return () => unsub();
  }, [id, product?.productImage?.imageItemID]);

  // Update document title
  useEffect(() => {
    if (product) {
      document.title = product.productName;
    }
  }, [product]);

  // Fetch ALL products (once)
  useEffect(() => {
    (async () => {
      const snap = await getDocs(collection(db, 'products'));
      const fetched = snap.docs.map(d => ({ id: d.id, ...d.data() }));
      setAllProducts(fetched);
    })();
  }, []);

  // Fetch ALL categories (once)
  useEffect(() => {
    (async () => {
      const snap = await getDocs(collection(db, 'categories'));
      const fetched = snap.docs.map(d => ({ id: d.id, ...d.data() }));
      setAllCategories(fetched);
    })();
  }, []);

  // Modal: ESC key listener & body overflow
  useEffect(() => {
    const handleKeyDown = e => {
      if (e.key === 'Escape' && previewImage) {
        closeModal();
      }
    };

    if (previewImage) {
      document.addEventListener('keydown', handleKeyDown);
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.body.style.overflow = 'unset';
    };
  }, [previewImage]);

  const handleImageClick = image => {
    setPreviewImage(image);
    setImageLoading(true);
    setTimeout(() => {
      setIsModalVisible(true);
    }, 10);
  };

  const handleModalImageLoad = () => {
    setImageLoading(false);
  };

  const closeModal = () => {
    setIsModalVisible(false);
    setImageLoading(false);
    setTimeout(() => {
      setPreviewImage(null);
    }, 200);
  };

  const handleModalClick = e => {
    if (e.target === e.currentTarget) {
      closeModal();
    }
  };

  const toggleModifierGroup = groupId => {
    setExpandedGroups(prev => {
      const isExpanded = prev?.[groupId] ?? true;
      return { ...prev, [groupId]: !isExpanded };
    });
  };

  // const handleImageLoad = productId => {
  //   setLoadedImages(prev => ({ ...prev, [productId]: true }));
  // };

  // Helper: get cover image for any product
  const getCoverImageForProduct = p => {
    if (p.productImage?.imageItemID) {
      const cover = allImages.find(img => img.id === p.productImage.imageItemID);
      if (cover) return cover;
    }
    return allImages.find(img => img.productID === p.id) || null;
  };

  const isLoading = !product;

  const emailSubject = product
    ? encodeURIComponent(`Question about ${product.productName}`)
    : '';

  // Build a map: productID → [categoryID, …]
  const productToCategories = {};
  allCategories.forEach(cat => {
    (cat.itemIDs || []).forEach(pid => {
      if (!productToCategories[pid]) productToCategories[pid] = [];
      productToCategories[pid].push(cat.id);
    });
  });

  // Current product's categories (array of category IDs)
  const currCats = product ? productToCategories[product.id] || [] : [];

  // Current product's price (in cents)
  const currPrice = product?.amount ?? 0;

  // Compute min/max price over all products
  const prices = allProducts.map(p => (typeof p.amount === 'number' ? p.amount : 0));
  const minPrice = Math.min(...prices);
  const maxPrice = Math.max(...prices);

  // Weights (sum should be 1)
  const weightPrice = 0.4;
  const weightCategory = 0.6;

  // Compute "suggestedProducts" by price / category similarity
  const suggestedProducts = product
    ? allProducts
        .filter(p => p.id !== product.id)
        .map(p => {
          const otherPrice = typeof p.amount === 'number' ? p.amount : 0;
          const priceDiff = Math.abs(currPrice - otherPrice);
          const priceNormalized = (maxPrice - minPrice) > 0
            ? priceDiff / (maxPrice - minPrice)
            : 0;
          const priceScore = 1 - priceNormalized;

          const otherCats = productToCategories[p.id] || [];
          const sharedCount = currCats.filter(c => otherCats.includes(c)).length;
          const unionCount = new Set([...currCats, ...otherCats]).size;
          const catScore = unionCount > 0 ? sharedCount / unionCount : 0;

          const combinedScore = weightPrice * priceScore + weightCategory * catScore;
          return { ...p, combinedScore };
        })
        .sort((a, b) => b.combinedScore - a.combinedScore)
        .slice(0, 6)
    : [];

  return (
    <div className="product-detail-container">
      {isLoading ? (
        <Loading />
      ) : (
        <>
      {/* Main Product Section */}
      <div className="product-main-section">
        {/* Left Column: Images */}
        <div className="images-column">
          {/* Cover Image */}
          {coverImage?.imageURL ? (
            <div className="cover-image-container">
              <FadeInImage
                src={coverImage.imageURL}
                alt={product.productName}
                onClick={() => handleImageClick(coverImage)}
                className="cover-image"
              />
            </div>
          ) : (
            <div className="cover-image-container">
              <div className="cover-placeholder">
                <span className="material-icons placeholder-icon">photo</span>
              </div>
            </div>
          )}

          {/* More Photos Gallery */}
          {productImages.length > 1 && (
            <div className="gallery-section">
              <div className="product-image-grid">
                {productImages.map(img => (
                  <div key={img.id} className="product-image-item">
                    <FadeInImage
                      src={img.imageURL}
                      alt={img.caption}
                      onClick={() => handleImageClick(img)}
                      className="gallery-image thumbnail-image"
                    />
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Right Column: Product Info */}
        <div className="product-info">
          {/* ← Insert categories here: */}
          {currCats.length > 0 && (
            <div className="product-categories">
              {currCats.map(catId => {
                const catObj = allCategories.find(c => c.id === catId);
                return catObj ? (
                  <Link
                    key={catObj.id}
                    to={`/category/${catObj.id}`}
                    className="category-label"
                  >
                    {catObj.title}
                  </Link>
                ) : null;
              })}
            </div>
          )}

          {/* Original product name */}
          <h1 className="product-title">{product.productName}</h1>

          {showPrices &&
           typeof product.amount === 'number' &&
           product.amount > 0 &&
           product.currency ? (
            <p className="product-price">
              {formatPrice(product.amount, product.currency)}
            </p>
          ) : null}

          {product.stockStatus && (
            <>
              <p className="stock-status">{product.stockStatus}</p>
              {(storePhone || storeEmail) && (
                <div className="contact-button-row">
                  {storePhone && (
                    <button
                      type="button"
                      className="call-store-button"
                      onClick={() => {
                        const digitsOnly = storePhone.replace(/\D+/g, '');
                        window.location.href = `tel:${digitsOnly}`;
                      }}
                      disabled={storeLoading}
                    >
                      {storeLoading ? (
                        'Loading…'
                      ) : (
                        <>
                          <span className="material-icons phone-icon">
                            phone
                          </span>
                          Call Store
                        </>
                      )}
                    </button>
                  )}
                  {storeEmail && (
                    <button
                      type="button"
                      className="email-store-button"
                      onClick={() => {
                        window.location.href = `mailto:${storeEmail}?subject=${emailSubject}`;
                      }}
                      disabled={storeLoading}
                    >
                      {storeLoading ? (
                        'Loading…'
                      ) : (
                        <>
                          <span className="material-icons email-icon">mail</span>
                          Email Store
                        </>
                      )}
                    </button>
                  )}
                </div>
              )}
            </>
          )}

          {/* Collapsible Description */}
          {product.description && product.description.trim() !== '' && (
            <div className="description-section">
              <div
                className="description-header"
                onClick={() => setShowDescription(prev => !prev)}
              >
                <h3 className="small-section-title">Description</h3>
                <span className="material-icons arrow-icon">
                  {showDescription ? 'expand_less' : 'expand_more'}
                </span>
              </div>
              <hr className="section-divider" />
              {showDescription && (
                <p className="product-description">{product.description}</p>
              )}
            </div>
          )}

          {Array.isArray(product.modifierGroups) &&
            product.modifierGroups.length > 0 && (
              <div className="modifier-groups-section">
                {product.modifierGroups.map(group => {
                  const isExpanded = expandedGroups[group.id] ?? true;
                  return (
                    <div key={group.id} className="description-section">
                      <div
                        className="description-header"
                        onClick={() => toggleModifierGroup(group.id)}
                      >
                        <h3 className="small-section-title">{group.title}</h3>
                        <span className="material-icons arrow-icon">
                          {isExpanded ? 'expand_less' : 'expand_more'}
                        </span>
                      </div>
                      <hr className="section-divider" />
                      {isExpanded &&
                        Array.isArray(group.modifierOptions) && (
                          <ul className="modifier-options-list">
                            {group.modifierOptions.map(option => {
                              const priceInfo = option.priceInfo || {};
                              const showOptionPrice =
                                showPrices &&
                                typeof priceInfo.price === 'number' &&
                                priceInfo.currency;
                              const stockStatus =
                                typeof option.stockStatus === 'string'
                                  ? option.stockStatus.trim()
                                  : '';
                              const isOutOfStock =
                                stockStatus.toLowerCase() === 'out of stock';
                              return (
                                <li key={option.id} className="modifier-option-item">
                                  <div className="modifier-option-header">
                                    <span className="modifier-option-title">
                                      {option.title}
                                    </span>
                                    {showOptionPrice && (
                                      <span className="modifier-option-price">
                                        {formatPrice(priceInfo.price, priceInfo.currency)}
                                      </span>
                                    )}
                                  </div>
                                  {isOutOfStock && (
                                    <p className="modifier-option-stock modifier-option-stock--out">
                                      {stockStatus || option.stockStatus}
                                    </p>
                                  )}
                                </li>
                              );
                            })}
                          </ul>
                        )}
                    </div>
                  );
                })}
              </div>
            )}

          {/* ----------------- “Specifications” SECTION (only show non-empty rows) ----------------- */}
          {product.identifiers && (
            <div className="description-section">
              <div
                className="description-header"
                onClick={() => setShowSpecs(prev => !prev)}
              >
                <h3 className="small-section-title">Specifications</h3>
                <span className="material-icons arrow-icon">
                  {showSpecs ? 'expand_less' : 'expand_more'}
                </span>
              </div>
              <hr className="section-divider" />
              {showSpecs && (
                <ul className="product-specs-list">
                  {product.identifiers.sku && product.identifiers.sku.trim() !== '' && (
                    <li>
                      <strong>SKU:</strong> {product.identifiers.sku}
                    </li>
                  )}
                  {product.identifiers.upc && product.identifiers.upc.trim() !== '' && (
                    <li>
                      <strong>UPC:</strong> {product.identifiers.upc}
                    </li>
                  )}
                  {product.identifiers.ean && product.identifiers.ean.trim() !== '' && (
                    <li>
                      <strong>EAN:</strong> {product.identifiers.ean}
                    </li>
                  )}
                  {product.identifiers.gtin && product.identifiers.gtin.trim() !== '' && (
                    <li>
                      <strong>GTIN:</strong> {product.identifiers.gtin}
                    </li>
                  )}
                  {/*
                    If all fields are empty, this <ul> will render nothing.
                    You can optionally show a “No specifications available” message by
                    checking each field above. For now, it simply omits empty rows.
                  */}
                </ul>
              )}
            </div>
          )}

          {product.deactivatedDate && (
            <p className="deactivated-date">
              No longer available as of {formatDate(product.deactivatedDate)}
            </p>
          )}
        </div>
      </div>

      {/* Suggested Products Section */}
      {suggestedProducts.length > 0 && (
        <>
          <hr className="suggested-divider" />
          <div className="suggested-section">
            <h2 className="section-title">You May Also Like</h2>
            <div className="suggested-grid">
              {suggestedProducts.map(sugg => (
                <ProductGridItem
                  key={sugg.id}
                  product={sugg}
                  coverImage={getCoverImageForProduct(sugg)}
                  onClick={() => navigate(buildProductPath(sugg))}
                />
              ))}
            </div>
          </div>
        </>
      )}

      {/* Image Preview Modal */}
      {previewImage && (
        <div
          className={`modal-overlay ${
            isModalVisible ? 'modal-visible' : ''
          }`}
          onClick={handleModalClick}
        >
          <button
            className="modal-close-button"
            onClick={closeModal}
            aria-label="Close preview"
          >
            ×
          </button>

          <div
            className={`modal-content ${
              isModalVisible ? 'modal-content-visible' : ''
            }`}
          >
            {imageLoading && (
              <div className="modal-placeholder">
                <div className="placeholder-spinner"></div>
                <p className="placeholder-text">Loading image...</p>
              </div>
            )}

            <img
              src={previewImage.imageURL}
              alt={previewImage.caption || product.productName}
              className={`modal-image ${
                imageLoading ? 'modal-image-loading' : ''
              }`}
              onLoad={handleModalImageLoad}
            />
            {previewImage.caption && !imageLoading && (
              <p className="modal-caption">{previewImage.caption}</p>
            )}
          </div>
        </div>
      )}

        </>
      )}
      <style jsx>{`
        /* Container */
        /*
         Keep this wrapper aligned with the home/contact pages.
         Do not add width: 100% plus box-sizing: border-box here: that makes the
         16px side padding count against the 1200px max width, which renders this
         page about 16px tighter per side on larger screens.
        */
        .product-detail-container {
          --product-detail-space: ${SPACING_BUTTON};
          max-width: ${MAX_CONTENT_WIDTH};
          margin: 0 auto;
          padding: ${SPACING_CARD_BODY} ${SPACING_BUTTON};
          padding-left: max(16px, env(safe-area-inset-left));
          padding-right: max(16px, env(safe-area-inset-right));
        }
        @media (max-width: 767px) {
          .product-detail-container {
            --product-detail-space: ${SPACING_COMPONENT};
            padding: ${SPACING_BUTTON};
            padding-left: max(16px, env(safe-area-inset-left));
            padding-right: max(16px, env(safe-area-inset-right));
          }
        }

        /* Main Section: flex-column on mobile, row on desktop */
        .product-main-section {
          display: flex;
          gap: ${SPACING_CARD_BODY};
          margin-bottom: ${SPACING_CARD_BODY};
          flex-direction: column;
        }
        @media (min-width: 768px) {
          .product-main-section {
            flex-direction: row;
            align-items: flex-start;
          }
        }

        /* Images Column */
        .images-column {
          flex: 1;
          max-width: 500px;
          display: flex;
          flex-direction: column;
          gap: var(--product-detail-space);
        }
        @media (max-width: 767px) {
          .images-column {
            max-width: none;
            width: 100%;
          }
        }

        /* Cover Image Container */
        .cover-image-container {
          width: 100%;
        }

        @keyframes shimmer {
          0% { background-position: -200% 0; }
          100% { background-position: 200% 0; }
        }

        /* Placeholder when no cover image */
        .cover-placeholder {
          position: relative;
          width: 100%;
          background-color: var(--color-border);
          border: 1px solid var(--color-border-strong);
          border-radius: ${BORDER_RADIUS};
          overflow: hidden;
        }
        .cover-placeholder::before {
          content: '';
          display: block;
          padding-top: 100%;
        }
        .cover-placeholder .placeholder-icon {
          position: absolute;
          top: 50%;
          left: 50%;
          transform: translate(-50%, -50%);
          font-size: 64px;
          opacity: 0.5;
          line-height: 1;
        }

        /* Image-Container & Fade-In */
        .image-container {
          position: relative;
          width: 100%;
          cursor: pointer;
          transition: transform 0.2s ease;
          border-radius: ${BORDER_RADIUS};
          overflow: hidden;
          border: 1px solid var(--color-border-strong);
          background: linear-gradient(90deg, #3a3a3a 25%, #4a4a4a 50%, #3a3a3a 75%);
          background-size: 200% 100%;
          animation: shimmer 1.5s ease-in-out infinite;
        }
        .image-container:hover {
          transform: translateY(-2px);
        }
        .image-container::before {
          content: '';
          display: block;
          padding-top: 100%;
        }
        /* Thumbnails */
        .thumbnail-image::before {
          padding-top: 100% !important;
        }
        .fade-img {
          position: absolute;
          top: 0;
          left: 0;
          width: 100%;
          height: 100%;
          object-fit: cover;
          opacity: 0;
          transition: opacity 0.3s ease-in-out;
        }
        .fade-img.loaded {
          opacity: 1;
        }
        .clickable-image {
          cursor: pointer;
        }

        /* Product Info */
        .product-info {
          flex: 1;
          min-width: 0;
          display: flex;
          flex-direction: column;
          gap: var(--product-detail-space);
        }
        @media (min-width: 768px) {
          .product-info > .product-categories,
          .product-info > .stock-status,
          .product-info > .description-section,
          .product-info > .modifier-groups-section,
          .product-info > .deactivated-date {
            margin-top: 0;
            margin-bottom: 0;
          }
        }

        /* Category labels */
        .product-categories {
          margin: 0;
        }
        .category-label {
          display: inline-block;
          font-size: ${FONT_FOOTNOTE};
          font-weight: 600;
          letter-spacing: 0.04em;
          text-transform: uppercase;
          color: var(--color-text-muted);
          margin-right: 10px;
          text-decoration: none;
          transition: color 0.2s ease;
        }
        .category-label:hover,
        .category-label:focus {
          color: var(--color-text-secondary);
          text-decoration: underline;
        }

        .product-title {
          margin: 0;
          font-size: 1.5rem;
          font-weight: 700;
          line-height: 1.2;
          letter-spacing: -0.01em;
          color: var(--color-text-primary);
        }
        .product-price {
          margin: 0;
          font-size: 1.5rem;
          font-weight: 600;
          line-height: 1.2;
          color: var(--color-accent);
        }

        /* Stock Status & Contact Buttons */
        .stock-status {
          margin: 0;
          font-size: ${FONT_FOOTNOTE};
          color: var(--color-text-muted);
        }
        .contact-button-row {
          display: flex;
          flex-wrap: wrap;
          gap: 12px;
          margin-top: 12px;
        }
        .call-store-button,
        .email-store-button {
          background-color: var(--color-accent);
          border: none;
          border-radius: ${BORDER_RADIUS};
          padding: 12px 18px;
          font-size: ${FONT_HEADLINE};
          color: var(--color-accent-text);
          cursor: pointer;
          display: inline-flex;
          align-items: center;
          justify-content: center;
          gap: 8px;
        }
        .call-store-button:hover:not(:disabled),
        .email-store-button:hover:not(:disabled) {
          background-color: var(--color-accent-hover);
        }
        .call-store-button:disabled,
        .email-store-button:disabled {
          opacity: 0.6;
          cursor: default;
          background-color: var(--color-badge-muted);
        }
        .phone-icon,
        .email-icon {
          font-size: 20px;
          line-height: 1;
        }

        /* Collapsible Description */
        .description-section {
          margin: 0;
        }
        .description-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          cursor: pointer;
          margin-bottom: var(--product-detail-space);
        }
        .small-section-title {
          margin: 0;
          font-size: clamp(1.125rem, 1.4vw, 1.25rem);
          font-weight: 700;
          line-height: 1.2;
          color: var(--color-text-primary);
        }
        .arrow-icon {
          font-size: 24px;
          line-height: 1;
        }
        .section-divider {
          margin: 0 0 var(--product-detail-space) 0;
          height: 2px;
          border: 0;
          background-color: var(--color-border);
        }
        .product-description {
          margin: 0;
          font-size: ${FONT_CALLOUT};
          line-height: 1.65;
          color: var(--color-text-secondary);
        }

        @media (max-width: 767px) {
          .category-label {
            font-size: 0.8rem;
          }
          .product-title {
            font-size: 1.5rem;
            line-height: 1.2;
          }
          .product-price {
            font-size: 1.5rem;
          }
          .small-section-title {
            font-size: 1.125rem;
          }
          .product-description {
            font-size: ${FONT_CALLOUT};
            line-height: 1.6;
          }
        }

        /* Product Specifications List */
        .product-specs-list {
          margin: 0;
          padding-left: 16px;
          font-size: ${FONT_BODY};
          color: var(--color-text-secondary);
          line-height: 1.6;
        }
        .product-specs-list li {
          margin-bottom: 8px;
        }
        .product-specs-list li strong {
          font-weight: 600;
          color: var(--color-text-primary);
        }

        .modifier-options-list {
          margin: 0;
          padding: 0;
          list-style: none;
        }
        .modifier-option-item {
          padding: 12px 0;
          border-bottom: 1px solid var(--color-border-subtle);
        }
        .modifier-option-item:last-child {
          border-bottom: none;
        }
        .modifier-option-header {
          display: flex;
          justify-content: space-between;
          align-items: baseline;
          gap: 12px;
        }
        .modifier-option-title {
          font-size: ${FONT_BODY};
          font-weight: 600;
          color: var(--color-text-primary);
        }
        .modifier-option-price {
          font-size: ${FONT_SUBHEADLINE};
          color: var(--color-text-secondary);
          white-space: nowrap;
        }
        .modifier-option-stock {
          margin: 6px 0 0 0;
          font-size: ${FONT_CALLOUT};
          color: var(--color-text-secondary);
        }
        .modifier-option-stock--out {
          color: var(--color-text-muted);
        }

        .deactivated-date {
          margin: 8px 0;
          font-size: ${FONT_FOOTNOTE};
          color: var(--color-text-muted);
        }

        /* Gallery Section */
        .gallery-section {
          margin-top: 0;
        }
        .product-image-grid {
          display: grid;
          gap: 12px;
          grid-template-columns: repeat(4, 1fr);
        }
        @media (min-width: 600px) {
          .product-image-grid {
            grid-template-columns: repeat(5, 1fr);
            gap: 14px;
          }
        }
        @media (max-width: 480px) {
          .product-image-grid {
            grid-template-columns: repeat(3, 1fr);
            gap: 10px;
          }
        }
        .product-image-item {
          display: flex;
          flex-direction: column;
        }

        /* Suggested Products Section */
        .suggested-divider {
          margin: var(--product-detail-space) 0;
          height: 2px;
          border: 0;
          background-color: var(--color-border);
          width: 100%;
        }
        .suggested-section {
          display: flex;
          flex-direction: column;
          gap: var(--product-detail-space);
        }
        .section-title {
          margin: 0;
        }
        .suggested-grid {
          display: grid;
          grid-template-columns: repeat(6, 1fr);
          gap: 16px;
          width: 100%;
        }
        @media (max-width: 999px) {
          .suggested-grid {
            grid-template-columns: repeat(4, 1fr);
          }
        }
        @media (max-width: 600px) {
          .suggested-grid {
            grid-template-columns: repeat(3, 1fr);
          }
        }

        /* Modal Overlay & Content */
        .modal-overlay {
          position: fixed;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background-color: rgba(0, 0, 0, 0);
          display: flex;
          align-items: center;
          justify-content: center;
          z-index: 1000;
          padding: 20px;
          opacity: 0;
          transition: opacity 0.2s ease, background-color 0.2s ease;
          pointer-events: none;
        }
        .modal-overlay.modal-visible {
          opacity: 1;
          background-color: rgba(0, 0, 0, 0.9);
          pointer-events: all;
        }

        .modal-close-button {
          position: absolute;
          top: 20px;
          right: 20px;
          background: rgba(0, 0, 0, 0.7);
          border: none;
          color: white;
          font-size: 32px;
          cursor: pointer;
          padding: 8px 12px;
          z-index: 1001;
          border-radius: 50%;
          width: 48px;
          height: 48px;
          display: flex;
          align-items: center;
          justify-content: center;
          transition: background-color 0.2s ease, transform 0.2s ease;
          line-height: 1;
        }
        .modal-close-button:hover {
          background-color: rgba(0, 0, 0, 0.9);
          transform: scale(1.1);
        }

        .modal-content {
          position: relative;
          max-width: 90vw;
          max-height: 90vh;
          display: flex;
          flex-direction: column;
          align-items: center;
          transform: scale(0.8);
          opacity: 0;
          transition: transform 0.2s ease, opacity 0.2s ease;
        }
        .modal-content.modal-content-visible {
          transform: scale(1);
          opacity: 1;
        }

        .modal-placeholder {
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          min-width: 300px;
          min-height: 200px;
          color: white;
        }
        .placeholder-spinner {
          width: 40px;
          height: 40px;
          border: 3px solid rgba(255, 255, 255, 0.3);
          border-top-color: white;
          border-radius: 50%;
          animation: spin 1s linear infinite;
          margin-bottom: 16px;
        }
        .placeholder-text {
          font-size: ${FONT_BODY};
          opacity: 0.8;
          margin: 0;
        }
        @keyframes spin {
          to {
            transform: rotate(360deg);
          }
        }

        .modal-image {
          max-width: 100%;
          max-height: 80vh;
          width: auto;
          height: auto;
          object-fit: contain;
          border-radius: 0;
          box-shadow: 0 8px 32px rgba(0, 0, 0, 0.3);
          border: 1px solid var(--color-border-strong);
          transition: opacity 0.2s ease;
        }
        .modal-image-loading {
          opacity: 0;
          position: absolute;
          pointer-events: none;
        }
        .modal-caption {
          margin-top: 16px;
          color: white;
          font-size: ${FONT_BODY};
          text-align: center;
          max-width: 600px;
          line-height: 1.4;
        }

        /*
         Scope box-sizing to the product detail subtree only.
         A global star selector box-sizing rule here can leak into shared layout
         like the navigation bar while this route is mounted.
        */
        .product-detail-container,
        .product-detail-container *,
        .product-detail-container *::before,
        .product-detail-container *::after {
          box-sizing: border-box;
        }

        @media (max-width: 600px) {
          .modal-close-button {
            top: 15px;
            right: 15px;
            font-size: 24px;
            width: 40px;
            height: 40px;
            padding: 6px 8px;
          }
          .modal-image {
            max-height: 75vh;
          }
          .modal-caption {
            font-size: ${FONT_CALLOUT};
            margin-top: 12px;
          }
          .modal-placeholder {
            min-width: 250px;
            min-height: 150px;
          }
          .placeholder-spinner {
            width: 32px;
            height: 32px;
            margin-bottom: 12px;
          }
          .placeholder-text {
            font-size: ${FONT_CALLOUT};
          }
        }
      `}</style>
    </div>
  );
}
